// The HQ console's routes (spec/14 §1), at the root of its own origin, `https://hq.{domain}/…`, with
// the console's sign-in and second factor in front (`routes/shared.tsx`). Every page is inside the HQ
// layout, which lets only a platform role with a second factor through to the shell (`HqLayout`).
//
// A screen that lands adds its route here and its section's `to` in `sections.ts`.

import { createRoute } from '@tanstack/react-router';

import { createSharedRoutes } from '../routes/shared';
import { AuditLogPage } from './audit/AuditLogPage';
import { BrandPage } from './BrandPage';
import { CataloguePage } from './catalogue/CataloguePage';
import { HqHome } from './HqHome';
import { HqLayout } from './HqLayout';
import { HqNotFound } from './HqNotFound';
import { FunnelPage } from './leads/FunnelPage';
import { leadFiltersFrom } from './leads/queries';
import { LeadPage } from './leads/LeadPage';
import { PipelinePage } from './leads/PipelinePage';
import { TodayPage } from './leads/TodayPage';
import { LivePage } from './live/LivePage';
import { NewOrganizationPage } from './organizations/NewOrganizationPage';
import { OrganizationPage } from './organizations/OrganizationPage';
import { OrganizationsPage } from './organizations/OrganizationsPage';
import { HQ_PATHS } from './paths';
import { MigrationPage } from './migration/MigrationPage';
import { MigrationRunPage } from './migration/MigrationRunPage';
import { HqOrganizationSettingsPage, PlatformSettingsPage } from './PlatformSettings';
import { ProvidersPage } from './ProvidersPage';
import { venueFiltersFrom } from './venues/queries';
import { VenuesPage } from './venues/VenuesPage';
import { PlayerPage } from './players/PlayerPage';
import { PlayerSearchPage, PlayersSection } from './players/PlayerSearch';
import { StationReleasesPage } from './releases/StationReleasesPage';
import { AddonCataloguePage } from './subscriptions/AddonCataloguePage';

export function createHqRouteTree() {
  const { rootRoute, signInRoute, signedInRoute, welcomeRoute } = createSharedRoutes({ notFound: HqNotFound });

  const hqRoute = createRoute({ getParentRoute: () => signedInRoute, id: 'hq', component: HqLayout });

  const home = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.home, component: HqHome });
  // HQ Live (spec/14 §2, F-ANL-02).
  const live = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.live, component: LivePage });

  // The sales pipeline (spec/14 §16, F-HQ-39): Today is its front page. `/leads/pipeline` and `/leads/funnel` outrank the id.
  const leads = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.leads, component: TodayPage });
  // The pipeline's filters are in its address; only what the api would take is kept.
  const pipeline = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.pipeline, validateSearch: leadFiltersFrom, component: PipelinePage });
  const funnel = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.funnel, component: FunnelPage });
  const lead = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.lead, component: LeadPage });

  // Organisations and venues (spec/14 §3.1, §3.3). `/organizations/new` outranks the id, as a path
  // with no parameter does.
  const organizations = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.organizations, component: OrganizationsPage });
  const newOrganization = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.newOrganization, component: NewOrganizationPage });
  const organization = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.organization, component: OrganizationPage });
  // Moving its players from another system (spec/14 §3.5, F-HQ-40): its moves, and one move's steps.
  const organizationMigration = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.organizationMigration, component: MigrationPage });
  const organizationMigrationRun = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.organizationMigrationRun, component: MigrationRunPage });
  // The list's filters are in its address; only what the api would take is kept.
  const venues = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.venues, validateSearch: venueFiltersFrom, component: VenuesPage });
  // The game catalogue (spec/14 §6): search on top, the mirrored games below.
  const catalogue = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.catalogue, component: CataloguePage });
  // Subscriptions → Add-ons (spec/14 §4.5, F-BOOT-17): the add-on catalogue and the price of each.
  const subscriptions = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.subscriptions, component: AddonCataloguePage });
  // Players (spec/14 §10): the search, and one player's platform record, under one frame that keeps the search.
  const players = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.players, component: PlayersSection });
  const playerSearch = createRoute({ getParentRoute: () => players, path: '/', component: PlayerSearchPage });
  const player = createRoute({ getParentRoute: () => players, path: '$playerId', component: PlayerPage });

  // Fleet: station client releases and their staged rollouts (spec/14 §12, F-STN-12).
  const fleet = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.fleet, component: StationReleasesPage });

  // The audit log (spec/14 §15, spec/07 §11.2): every organisation's and the platform's, with a reason.
  const audit = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.audit, component: AuditLogPage });

  // Platform settings (spec/14 §14): the platform's defaults, the brand, one organisation's.
  const settings = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.settings, component: PlatformSettingsPage });
  const settingsGroup = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.settingsGroup, component: PlatformSettingsPage });
  const brand = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.brand, component: BrandPage });
  // The external providers and their checks (spec/14 §15, F-HQ-38), a tab of Platform settings.
  const providers = createRoute({ getParentRoute: () => hqRoute, path: HQ_PATHS.providers, component: ProvidersPage });
  const organizationSettings = createRoute({
    getParentRoute: () => hqRoute,
    path: HQ_PATHS.organizationSettings,
    component: HqOrganizationSettingsPage,
  });
  const organizationSettingsGroup = createRoute({
    getParentRoute: () => hqRoute,
    path: HQ_PATHS.organizationSettingsGroup,
    component: HqOrganizationSettingsPage,
  });

  return rootRoute.addChildren([
    signInRoute,
    welcomeRoute,
    signedInRoute.addChildren([
      hqRoute.addChildren([
        home,
        live,
        leads,
        pipeline,
        funnel,
        lead,
        organizations,
        newOrganization,
        organization,
        organizationMigration,
        organizationMigrationRun,
        venues,
        catalogue,
        subscriptions,
        players.addChildren([playerSearch, player]),
        fleet,
        settings,
        settingsGroup,
        brand,
        providers,
        organizationSettings,
        organizationSettingsGroup,
        audit,
      ]),
    ]),
  ]);
}
